import { useState, useEffect, useRef } from "react";

// Easing functions (simplified from easings.js)
const easing = {
  sineOut: (t) => Math.sin((t * Math.PI) / 2),
  exponentialInOut: (t) =>
    t === 0 || t === 1
      ? t
      : t < 0.5
        ? 0.5 * Math.pow(2, 10 * (2 * t - 1))
        : 0.5 * (-Math.pow(2, -10 * (2 * t - 1)) + 2),
};

// ShapeOverlays component for SVG animation
const ShapeOverlays = ({ isOpen, onAnimationComplete }) => {
  const pathRefs = useRef([]);
  const numPoints = 4;
  const duration = 1000;
  const delayPointsArray = Array(numPoints).fill(0);
  const delayPerPath = 60;
  const delayPointsMax = 0;
  let timeStart = Date.now();
  let isAnimating = false;

  const updatePath = (time) => {
    const points = [];
    for (let i = 0; i < numPoints; i++) {
      const thisEase = i % 2 === 1 ? easing.sineOut : easing.exponentialInOut;
      points[i] =
        (1 -
          thisEase(
            Math.min(Math.max(time - delayPointsArray[i], 0) / duration, 1),
          )) *
        100;
    }

    let str = "";
    str += isOpen ? `M 0 0 H ${points[0]}` : `M ${points[0]} 0`;
    for (let i = 0; i < numPoints - 1; i++) {
      const p = ((i + 1) / (numPoints - 1)) * 100;
      const cp = p - ((1 / (numPoints - 1)) * 100) / 2;
      str += `C ${points[i]} ${cp} ${points[i + 1]} ${cp} ${
        points[i + 1]
      } ${p} `;
    }
    str += isOpen ? `H 100 V 0` : `H 0 V 0`;
    return str;
  };

  const render = () => {
    if (isOpen) {
      pathRefs.current.forEach((path, i) => {
        path.setAttribute(
          "d",
          updatePath(Date.now() - (timeStart + delayPerPath * i)),
        );
      });
    } else {
      pathRefs.current.forEach((path, i) => {
        path.setAttribute(
          "d",
          updatePath(
            Date.now() -
              (timeStart + delayPerPath * (pathRefs.current.length - i - 1)),
          ),
        );
      });
    }
  };

  const renderLoop = () => {
    render();
    if (
      Date.now() - timeStart <
      duration + delayPerPath * (pathRefs.current.length - 1) + delayPointsMax
    ) {
      requestAnimationFrame(renderLoop);
    } else {
      isAnimating = false;
      onAnimationComplete();
    }
  };

  useEffect(() => {
    timeStart = Date.now();
    isAnimating = true;
    renderLoop();
    return () => {
      isAnimating = false;
    };
  }, [isOpen]);

  return (
    <svg
      className="fixed inset-0 w-full h-full z-30 pointer-events-none"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      ref={(el) => {
        if (el) {
          pathRefs.current = el.querySelectorAll("path");
        }
      }}
    >
      <path className="fill-[var(--home-primary)]" />
      <path className="fill-[var(--home-accent)]" />
    </svg>
  );
};

// Hamburger button component
const Hamburger = ({ isOpen, toggleMenu, darkBg }) => {
  const closedColor = darkBg ? "bg-white" : "bg-[var(--home-primary)]";
  return (
    <div
      className={`fixed top-10 lg:top-16 right-10 lg:right-16 z-50 cursor-pointer w-8 h-8 ${
        isOpen ? "is-opened-navi" : ""
      }`}
      onClick={toggleMenu}
      role="button"
      aria-label={isOpen ? "Close menu" : "Open menu"}
    >
      <div className="relative w-full h-full">
        <div
          className={`absolute w-full h-1 rounded-full top-0 transition-transform duration-300 ${
            isOpen ? "bg-[var(--home-cta-dark)] rotate-45 translate-y-3" : closedColor
          }`}
        />
        <div
          className={`absolute w-full h-1 rounded-full top-3 transition-opacity duration-300 ${
            isOpen ? "bg-[var(--home-cta-dark)] opacity-0" : closedColor
          }`}
        />
        <div
          className={`absolute w-full h-1 rounded-full top-6 transition-transform duration-300 ${
            isOpen ? "bg-[var(--home-cta-dark)] -rotate-45 -translate-y-3" : closedColor
          }`}
        />
      </div>
    </div>
  );
};

// Global menu component
const MENU_ITEMS = [
  { label: "Home", href: "/" },
  { label: "Skills", href: "/skills" },
  { label: "Work", href: "/work" },
  { label: "Experiments", href: "/experiments" },
];

const GlobalMenu = ({ isOpen }) => {
  return (
    <div
      className={`fixed inset-0 flex items-center justify-center z-40 transition-opacity duration-[2s] ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="text-center font-milker">
        {MENU_ITEMS.map(({ label, href }, i) => {
          return (
            <a
              key={i}
              href={href}
              className={`block text-[36px] lg:text-[56px] font-bold text-[var(--home-cta-dark)] hover:text-[var(--home-primary)] mb-4 transition-colors transition-opacity duration-[2000] delay-${
                i * 100
              } ${isOpen ? "opacity-100" : "opacity-0 "}`}
            >
              {label}
            </a>
          );
        })}
      </div>
    </div>
  );
};

// Main fixed nav menu component
const ShapeOverlayMenu = ({ darkBg = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const toggleMenu = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setIsOpen(!isOpen);
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
  };

  return (
    <>
      <Hamburger isOpen={isOpen} toggleMenu={toggleMenu} darkBg={darkBg} />
      <GlobalMenu isOpen={isOpen} />
      <ShapeOverlays
        isOpen={isOpen}
        onAnimationComplete={handleAnimationComplete}
      />
    </>
  );
};

export default ShapeOverlayMenu;
